import { TypographyP2 } from '@heidi/design-system/typography';
import { type LucideIcon } from 'lucide-react';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { ApprovalChangeField } from './approval-change-field';
import { InlineApprovalActions } from './inline-approval-actions';
import { InlineApprovalCardShell } from './inline-approval-card-shell';
import { RoutineInstructionsChanges } from './routine-instructions-changes';
import { DetailsSection } from './routine-proposal-details';

export type RoutineProposalKind = 'create' | 'update';

export type RoutineProposalDiffKey = 'name' | 'instructions' | 'schedule' | 'triggers';

export interface RoutineProposalDiff {
  key: RoutineProposalDiffKey;
  before: string;
  after: string;
}

export interface RoutineProposalCardProps {
  kind: RoutineProposalKind;
  /** Current name in the title. A rename lives in ``diffs``, not here. */
  name: string;
  summary?: string;
  Icon: LucideIcon;
  /** Create only — the prompt, shown in Details with no field labels. */
  instructions?: string;
  /** Update only — changed fields, already ordered. */
  diffs?: RoutineProposalDiff[];
  onApprove: () => void;
  onDeny: () => void;
  pendingVerdict: 'approve' | 'deny' | null;
  disabled?: boolean;
  approveDisabled?: boolean;
  'data-testid'?: string;
  approveTestId?: string;
  denyTestId?: string;
  nameTestId?: string;
}

const DIFF_LABEL_IDS: Record<
  RoutineProposalDiffKey,
  { defaultMessage: string; id: string; description: string }
> = {
  name: {
    defaultMessage: 'Name',
    id: 'HAlOn1ZsuY',
    description: 'Field label for a routine name change',
  },
  instructions: {
    defaultMessage: 'Instructions',
    id: 'RdHiqqnkU2',
    description: 'Field label for a routine instructions change',
  },
  schedule: {
    defaultMessage: 'Schedule',
    id: 'IprMp2N2pE',
    description: 'Field label for a routine schedule change',
  },
  triggers: {
    defaultMessage: 'Triggers',
    id: '0ECESdrp67',
    description: 'Field label for a routine event-trigger change',
  },
};

export const RoutineProposalCard = ({
  kind,
  name,
  summary,
  Icon,
  instructions,
  diffs = [],
  onApprove,
  onDeny,
  pendingVerdict,
  disabled = false,
  approveDisabled = false,
  'data-testid': dataTestId,
  approveTestId = 'create-workflow-approval-approve',
  denyTestId = 'create-workflow-approval-cancel',
  nameTestId = 'create-workflow-approval-name',
}: RoutineProposalCardProps) => {
  const intl = useIntl();
  const [detailsOpen, setDetailsOpen] = useState(true);
  const actionLabel =
    kind === 'create'
      ? intl.formatMessage({
          defaultMessage: 'Create routine',
          id: '+5D7GrB7eq',
        })
      : intl.formatMessage({
          defaultMessage: 'Update routine',
          id: 'k5wRcSJ2Yf',
          description: 'Title action for an update-routine approval card',
        });
  const approveLabel =
    kind === 'create'
      ? intl.formatMessage({
          defaultMessage: 'Create',
          id: 'ZVL1zMpqHe',
          description: 'Primary CTA to approve creating a routine from chat',
        })
      : intl.formatMessage({
          defaultMessage: 'Update',
          id: 'hIgCQIOYja',
          description: 'Primary CTA to approve updating a routine from chat',
        });

  const title = (
    <>
      <span>{actionLabel}</span>
      <span>:</span>{' '}
      <span className="font-normal" data-testid={nameTestId}>
        {name}
      </span>
    </>
  );

  const hasDetails = kind === 'create' ? Boolean(instructions) : diffs.length > 0;

  return (
    <InlineApprovalCardShell
      Icon={Icon}
      title={title}
      subtitle={summary || undefined}
      urgent={!disabled && pendingVerdict === null}
      data-testid={dataTestId}
    >
      {hasDetails ? (
        <DetailsSection
          open={detailsOpen}
          onToggle={() => setDetailsOpen((current) => !current)}
        >
          {kind === 'create' && instructions ? (
            <TypographyP2 className="whitespace-pre-line text-text-secondary">
              {instructions}
            </TypographyP2>
          ) : (
            <div className="flex flex-col gap-5">
              {diffs.map((diff) => (
                <DiffField
                  key={diff.key}
                  field={diff.key}
                  label={intl.formatMessage(DIFF_LABEL_IDS[diff.key])}
                  before={diff.before}
                  after={diff.after}
                />
              ))}
            </div>
          )}
        </DetailsSection>
      ) : null}
      <InlineApprovalActions
        onApprove={onApprove}
        onDeny={onDeny}
        pendingVerdict={pendingVerdict}
        disabled={disabled}
        approveDisabled={approveDisabled}
        approveLabel={approveLabel}
        approveTestId={approveTestId}
        denyTestId={denyTestId}
      />
    </InlineApprovalCardShell>
  );
};

const DiffField = ({
  field,
  label,
  before,
  after,
}: {
  field: RoutineProposalDiffKey;
  label: string;
  before: string;
  after: string;
}) => {
  if (field === 'instructions') {
    return (
      <div className="flex flex-col gap-2">
        <TypographyP2 className="text-text-primary">{label}</TypographyP2>
        <RoutineInstructionsChanges before={before} after={after} />
      </div>
    );
  }
  return <ApprovalChangeField label={label} before={before} after={after} />;
};
